import { format, parseISO } from 'date-fns';
import { List, Map } from 'immutable';
import _ from 'lodash';
import { createSelector } from 'reselect';

import { getSelectedStructure } from '@sophocles/features/sites/slices/selectedStructure/selectors';

export const relayControllerStatusHistorySelector = state => state.entities.relayControllerStatusHistories;

const addUnitsToReading = (values, unit) => (values ? _.map(values, data => data + ' ' + unit) : null);
const moveValuesToArray = (values, arr) => (values ? _.forEach(values, (value, key) => arr[key].push(value)) : null);

export const getLimitedRelayControllerStatusHistoryToUnitMapping = createSelector(
  relayControllerStatusHistorySelector,
  histories => {
    if (typeof histories === 'undefined') {
      return Map();
    }
    let myMap = Map();

    histories.forEach(history => {
      const max_arbitrary_units = history.get('relay_max_currents_arbitrary_units', null);
      let max_amps = history.get('relay_max_currents_amps', null);
      let max_watts = history.get('relay_max_power_watts', null);
      const instantaneous_arbitrary_units = history.get('relay_instantaneous_currents_arbitrary_units', null);
      let instantaneous_amps = history.get('relay_instantaneous_currents_amps', null);
      let instantaneous_watts = history.get('relay_instantaneous_power_watts', null);

      const receiveTime = history.get('receive_time');
      const status_type = history.get('status_type');

      max_amps = addUnitsToReading(max_amps, '(A)');
      max_watts = addUnitsToReading(max_watts, '(W)');

      instantaneous_amps = addUnitsToReading(instantaneous_amps, '(A)');
      instantaneous_watts = addUnitsToReading(instantaneous_watts, '(W)');

      let holderArray = [[], [], [], []];
      moveValuesToArray(max_arbitrary_units, holderArray);
      moveValuesToArray(instantaneous_arbitrary_units, holderArray);
      moveValuesToArray(max_amps, holderArray);
      moveValuesToArray(instantaneous_amps, holderArray);
      moveValuesToArray(max_watts, holderArray);
      moveValuesToArray(instantaneous_watts, holderArray);

      holderArray = _.map(holderArray, item => (item.length == 0 ? ['N/A'] : item));

      const limitedHistory = Map({
        id: history.get('id', '0'),
        receive_time: parseISO(receiveTime),
        display_time: format(parseISO(receiveTime), 'Pp'),
        status_type,
        relay_1_power: _.join(holderArray[0], ', '),
        relay_2_power: _.join(holderArray[1], ', '),
        relay_3_power: _.join(holderArray[2], ', '),
        relay_4_power: _.join(holderArray[3], ', '),
        battery_voltage: history.get('battery_voltage', 0),
        cpu_temperature: history.get('cpu_temperature', 0),
        cpu_input_voltage: history.get('cpu_input_voltage', 0),
        thermistor_temperature: history.get('thermistor_temperature', 0),
      });
      const unit_id = history.get('relay_controller', '0').toString();
      myMap = myMap.set(unit_id, myMap.get(unit_id, List()).push(limitedHistory));
    });
    return myMap;
  },
);

export const getLimitedRelayControllerStatusHistoryForSelectedSite = createSelector(
  relayControllerStatusHistorySelector,
  getSelectedStructure,
  (histories, site) => {
    const unit_id = site.getIn(['relay_controller', 'id'], 0);
    return histories
      .filter(history => history.get('relay_controller', -1) == unit_id)
      .toList()
      .map(history => {
        const relay_max_currents_arbitrary_units = history.get('relay_max_currents_arbitrary_units', null);
        const relay_max_currents_amps = history.get('relay_max_currents_amps', null);
        const relay_max_power_watts = history.get('relay_max_power_watts', null);
        const relay_instantaneous_currents_arbitrary_units = history.get(
          'relay_instantaneous_currents_arbitrary_units',
          null,
        );
        const relay_instantaneous_currents_amps = history.get('relay_instantaneous_currents_amps', null);
        const relay_instantaneous_power_watts = history.get('relay_instantaneous_power_watts', null);
        const receiveTime = history.get('receive_time');
        const status_type = history.get('status_type', 0);
        const positiveConsumedEnergy = history.get('positive_consumed_energy', 0);
        const generatedEnergy = history.get('generated_energy', 0);
        return Map({
          id: history.get('id', '0'),
          receive_time: parseISO(receiveTime),
          display_time: format(parseISO(receiveTime), 'Pp'),
          status_type,
          relay_max_currents_arbitrary_units,
          relay_max_currents_amps,
          relay_max_power_watts,
          relay_instantaneous_currents_arbitrary_units,
          relay_instantaneous_currents_amps,
          relay_instantaneous_power_watts,
          moment: parseISO(receiveTime),
          short: format(parseISO(receiveTime), 'MM/dd'),
          version: history.get('version', null),
          battery_voltage: history.get('battery_voltage', 0),
          cpu_temperature: history.get('cpu_temperature', 0),
          cpu_input_voltage: history.get('cpu_input_voltage', 0),
          thermistor_temperature: history.get('thermistor_temperature', 0),
          input_voltage_1: history.get('input_voltage_1', 0),
          input_voltage_2: history.get('input_voltage_2', 0),
          positive_consumed_energy: positiveConsumedEnergy,
          generated_energy: generatedEnergy,
        });
      });
  },
);
